import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
import { Box, Container, Typography, TextField, Alert, CircularProgress } from '@mui/material';
// components
import Page from '../../components/Page';
import { LoadingButton } from '@mui/lab';
import { RootState, useSelector } from './../../redux/store';
import axios from '../../utils/axios';
import { useSnackbar } from 'notistack';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import MIconButton from 'components/@material-extend/MIconButton';
import { useTranslation } from 'react-i18next';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import useSettings from 'hooks/useSettings';
import { DeliveryRequest, DeliveryRequestItem } from '../../@types/model/deliveryRequest';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { deliveryRequestState, getDeliveryRequests } from 'recoil/deliveryRequestState';
import '../../assets/css/manageOrder.scss';
import { ProductType } from '../../@types/model/productType';
import { getProductTypes, productTypeState } from 'recoil/productTypeState';
import { Chip } from '@mui/material';

// ----------------------------------------------------------------------

export default function ManageOrder() {
  const { themeStretch } = useSettings();
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDeliveryRequest, setLoadingDeliveryRequest] = useState(false);
  const [comment, setComment] = useState<string>('');
  const { workspace } = useSelector((state: RootState) => state.workspace);
  const { guid } = useParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deliveryRequestSelected, setDeliveryRequestSelected] = useState<DeliveryRequest | null>(
    null
  );

  const deliveryRequest: DeliveryRequest[] = useRecoilValue(deliveryRequestState);
  const setDeliveryRequest = useSetRecoilState(deliveryRequestState);
  const productTypes: ProductType[] = useRecoilValue(productTypeState);
  const setProductTypes = useSetRecoilState(productTypeState);

  useEffect(() => {
    setLoadingDeliveryRequest(true);
    getDeliveryRequests(guid).then((result: any) => {
      setDeliveryRequest(result.deliveryRequests);
      result.deliveryRequests.forEach((dr: DeliveryRequest) => {
        if (dr.guid === guid) {
          setDeliveryRequestSelected(dr);
        }
      });
      setLoadingDeliveryRequest(false);
    });
  }, []);

  useEffect(() => {
    getProductTypes().then((productTypes: ProductType[]) => setProductTypes(productTypes));
  }, []);

  const status = () => {
    switch (deliveryRequestSelected?.state) {
      case 'created':
        return <Chip label={t('created')} color="warning" />;
      case 'confirmed':
        return <Chip label={t('confirmed')} color="primary" />;
      case 'delivered':
        return <Chip label={t('delivered')} color="success" />;
    }
    return <span></span>;
  };

  return (
    <Page title={t('confirm_order')} style={{ marginTop: '30px' }}>
      <Container maxWidth={themeStretch ? false : 'xl'} style={{ width: '500px' }}>
        <Box sx={{ maxWidth: 480 }}>
          <Typography variant="h4" paragraph>
            {t('confirm_order')} {deliveryRequestSelected?.user_data?.name}
          </Typography>
          <Typography variant="h6" paragraph>
            {t('product_list')}
          </Typography>
          {loadingDeliveryRequest ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                marginBottom: '20px'
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <div>
              <div className="items">
                {deliveryRequestSelected?.items?.map((item: DeliveryRequestItem) => {
                  return (
                    <div key={item.guid} className="item">
                      {item.device_data?.attributes.name} -{' '}
                      <b>
                        {item.quantity}
                        {deliveryRequestSelected.delivery_configuration_data?.units} {t('of') + ' '}
                        {productTypes.find((pt: ProductType) => pt.guid === item.productType)?.name}
                      </b>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginBottom: '20px' }}>
                <b>{t('status')}</b>: {status()}
              </div>
            </div>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('link_incorrect_or_timed_out')}.
            </Alert>
          )}

          <Typography sx={{ color: 'text.secondary', mb: 2, mt: 0 }}>
            {t('confirm_the_reception_of_delivery_request')}.
          </Typography>
          <TextField
            id="outlined-multiline-static"
            label={t('insert_comment')}
            multiline
            rows={4}
            sx={{ width: '100%' }}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            disabled={deliveryRequestSelected?.state !== 'created'}
            sx={{ my: 2 }}
            onClick={() => {
              setLoading(true);
              axios
                .put(`/api/v2/delivery-request/` + guid, {
                  guid: guid,
                  delivery_configuration: deliveryRequestSelected?.delivery_configuration,
                  items: deliveryRequestSelected?.items?.map((item) => {
                    return {
                      ...item,
                      product_type_guid: item.productType,
                      product_schedule_item_guid: item.productScheduleItem
                    };
                  }),
                  state: 'confirmed',
                  comment: comment,
                  user: deliveryRequestSelected?.user,
                  delivery_date: deliveryRequestSelected?.delivery_date
                })
                .then(() => {
                  enqueueSnackbar(t('delivery_request_received_confirmed'), {
                    variant: 'success',
                    action: (key) => (
                      <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                      </MIconButton>
                    )
                  });
                  setLoading(false);
                  getDeliveryRequests(guid).then((result: any) => {
                    setDeliveryRequest(result.deliveryRequests);
                    result.deliveryRequests.forEach((dr: DeliveryRequest) => {
                      if (dr.guid === guid) {
                        setDeliveryRequestSelected(dr);
                      }
                    });
                    setComment('');
                  });
                })
                .catch((error) => {
                  setLoading(false);
                });
            }}
          >
            {t('confirm_reception')}
          </LoadingButton>
        </Box>
      </Container>
    </Page>
  );
}
